import { Dispatch, ReactNode, SetStateAction } from 'react';
import { ButtonColor, Theme, ValueOf } from '@redsift/design-system';
import { useToggletip } from './useToggletip';

/**
 * Context props.
 */
export type HiddenToggletipState = {
  /** Whether the toggletip should be hidden or removed from the DOM. */
  hideInsteadOfClose?: boolean;
  /** Method to set the hideInsteadOfClose prop. */
  setHideInsteadOfClose?: Dispatch<SetStateAction<boolean>>;
};
export type ToggletipState =
  | (ReturnType<typeof useToggletip> & {
      /** Class name to append to the trigger. */
      readonly triggerClassName?: string;
    } & HiddenToggletipState)
  | null;

/**
 * Component variant.
 */
export const ToggletipPlacement = {
  top: 'top',
  right: 'right',
  bottom: 'bottom',
  left: 'left',
  'top-start': 'top-start',
  'top-end': 'top-end',
  'right-start': 'right-start',
  'right-end': 'right-end',
  'bottom-start': 'bottom-start',
  'bottom-end': 'bottom-end',
  'left-start': 'left-start',
  'left-end': 'left-end',
} as const;
export type ToggletipPlacement = ValueOf<typeof ToggletipPlacement>;

/**
 * Component props.
 */
export interface ToggletipProps {
  /** Toggletip content width. Can be either based on the trigger, the available space or define with a number of pixels. */
  width?: 'trigger-width' | 'available-width' | number;
  /** Toggletip content min width. Can be either based on the trigger, the available space or define with a number of pixels. */
  minWidth?: 'trigger-width' | 'available-width' | number;
  /** Toggletip content max width. Can be either based on the trigger, the available space or define with a number of pixels. */
  maxWidth?: 'trigger-width' | 'available-width' | number;
  /** Button color that will be forward to the trigger. */
  color?: ButtonColor;
  /** Children. Can only be ToggletipTrigger and ToggletipContent. */
  children: ReactNode;
  /**
   * Default open status.
   * Used for uncontrolled version.
   */
  defaultOpen?: boolean;
  /** Delay time (in ms) for the tooltip to show up. */
  delay?: number;
  /**
   * Default placement of the toggletip.
   * @default 'bottom'
   */
  placement?: ToggletipPlacement | 'client-point';
  /** Whether the toggletip is a modal or not. */
  isModal?: boolean;
  /**
   * Whether the component is opened or not.
   * Used for controlled version.
   */
  isOpen?: boolean;
  /**
   * Space between trigger and content (in pixels).
   * @default 8
   */
  offset?: number;
  /** Method to handle component change. */
  onOpen?: (open: boolean) => void;
  /** Allows other components to be treated as trigger and content. */
  overrideDisplayName?: {
    content?: string;
    trigger?: string;
  };
  /** Id to the toggletip content. */
  toggletipId?: string;
  /** Role to apply to the toggletip. */
  role?: 'dialog';
  /** Theme. */
  theme?: Theme;
  /** Class name to append to the trigger. */
  triggerClassName?: string;
}

export type StyledToggletipProps = ToggletipProps;
